import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  CalendarDaysIcon, ClockIcon, CheckCircleIcon,
  ExclamationTriangleIcon, ArrowRightIcon,
} from '@heroicons/react/24/outline';
import publicClient from '../../api/publicClient';

// Groups a flat slot list by YYYY-MM-DD so the picker can render one
// "day card" with N time buttons inside.
function groupByDay(slots) {
  const days = {};
  for (const s of slots) {
    const dt = new Date(s.start_at);
    const dayKey = dt.toISOString().slice(0, 10);
    if (!days[dayKey]) days[dayKey] = { date: dt, slots: [] };
    days[dayKey].slots.push(s);
  }
  return Object.values(days).sort((a, b) => a.date - b.date);
}

function fmtDay(d) {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}
function fmtTime(s) {
  return new Date(s).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function SchedulePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [booking, setBooking] = useState(false);
  const [bookedView, setBookedView] = useState(null);

  useEffect(() => {
    let cancelled = false;
    publicClient.get(`/api/schedule/${token}`)
      .then(({ data }) => {
        if (cancelled) return;
        setData(data);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.data?.error || 'This link is invalid or expired.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const days = useMemo(() => groupByDay(data?.slots || []), [data]);

  const book = async () => {
    if (!selected) return;
    setBooking(true);
    try {
      await publicClient.post(`/api/schedule/${token}/book`, { slot_id: selected.id });
      setBookedView(selected);
    } catch (err) {
      toast.error(err.response?.data?.error || 'That time was just booked — please pick another.');
      // Refresh slot list so the now-taken one disappears.
      try {
        const { data } = await publicClient.get(`/api/schedule/${token}`);
        setData(data);
        setSelected(null);
      } catch {}
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-900 flex items-center justify-center"><div className="text-gray-400">Loading…</div></div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <Toaster position="top-right" />
        <div className="max-w-md text-center">
          <div className="bg-red-500/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="w-12 h-12 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Link unavailable</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  // Gate: prerequisites not done. Redirect them to /continue/:token which
  // routes to whichever step they're missing.
  if (!data?.progress?.attestation_completed || !data?.progress?.survey_completed) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h2 className="text-2xl font-bold text-white mb-3">A couple steps first</h2>
          <p className="text-gray-400 mb-6">
            Before scheduling an interview, please finish the attestation and questionnaire.
          </p>
          <Link
            to={`/continue/${token}`}
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-xl"
          >
            Continue your application <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  // Already-booked path (either fresh from booking or arriving back at the page)
  const booked = bookedView || data?.booked_interview;
  if (booked) {
    const startAt = bookedView?.start_at || booked.scheduled_at;
    const niceDate = fmtDay(new Date(startAt));
    const niceTime = fmtTime(startAt);
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <Toaster position="top-right" />
        <div className="max-w-md text-center">
          <div className="bg-green-500/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <CheckCircleIcon className="w-12 h-12 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">You're booked, {data?.candidate?.first_name}!</h2>
          <p className="text-gray-300 mb-2"><strong>{niceDate}</strong> at <strong>{niceTime}</strong></p>
          <p className="text-gray-500 text-sm mt-3">
            Calendar invite is on its way to your inbox. Need to reschedule? Reply to that email or call (574) 855-1604.
          </p>
        </div>
      </div>
    );
  }

  // No slots at all
  if (days.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <Toaster position="top-right" />
        <div className="max-w-md text-center">
          <CalendarDaysIcon className="w-16 h-16 text-orange-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">No interview times available yet</h2>
          <p className="text-gray-400">
            Thanks {data?.candidate?.first_name}! We don't have open interview times posted right now. We'll reach out as soon as we do.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <Toaster position="top-right" />
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">BB</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Pick your interview time</h1>
          <p className="text-gray-400 mt-1">
            Hi {data?.candidate?.first_name} — choose any open slot below.
          </p>
        </div>

        <div className="space-y-6">
          {days.map(day => (
            <div key={day.date.toISOString()} className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <CalendarDaysIcon className="w-5 h-5 text-orange-400" />
                <h3 className="text-white font-semibold">{fmtDay(day.date)}</h3>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {day.slots.map(s => {
                  const isSelected = selected?.id === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSelected(s)}
                      className={`flex items-center justify-center gap-1.5 rounded-lg py-2 px-3 text-sm font-medium border transition-colors ${
                        isSelected
                          ? 'bg-orange-500/20 border-orange-400 text-orange-300'
                          : 'bg-gray-700/30 border-gray-600 text-gray-200 hover:border-gray-500'
                      }`}
                    >
                      <ClockIcon className="w-4 h-4" />
                      {fmtTime(s.start_at)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {selected && (
          <div className="sticky bottom-4 mt-6 bg-gray-800/95 backdrop-blur border border-orange-500/30 rounded-2xl p-4 shadow-lg">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Your pick</div>
                <div className="text-white font-medium">
                  {fmtDay(new Date(selected.start_at))} at {fmtTime(selected.start_at)}
                </div>
              </div>
              <button
                type="button"
                onClick={book}
                disabled={booking}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white font-semibold px-5 py-2.5 rounded-xl"
              >
                {booking ? 'Booking…' : 'Confirm this time'}
              </button>
            </div>
          </div>
        )}

        <p className="text-center text-gray-600 text-sm mt-8">
          Bright Beginnings Childcare — Confidential
        </p>
      </div>
    </div>
  );
}
